import React, { useState, useEffect, useRef, useContext } from 'react';
import { WorkspaceContextSetter } from '../context/WorkspaceContext';

// ─── Layer color map ────────────────────────────────────────────────────────
const LAYER_COLORS = {
  absorber: '#00d4ff',
  buffer:   '#39d353',
  window:   '#f0a500',
  ETL:      '#a78bfa',
  HTL:      '#fb7185',
  contact:  '#c084fc',
  custom:   '#94a3b8',
};
function layerColor(cat) { return LAYER_COLORS[cat] || LAYER_COLORS.custom; }

// ─── Material categories (for selector grouping) ────────────────────────────
const MATERIAL_CATEGORIES = {
  absorber: ['CdTe','CdTe_CSU','CdTe_n','CdSeTe','CIGS','Perovskite','Silicon'],
  buffer:   ['CdS','MgZnO'],
  window:   ['ZnO_TCO','SnO2_FTO'],
  ETL:      ['SnO2'],
  HTL:      ['Spiro_OMeTAD'],
  contact:  ['Te'],
  custom:   ['custom'],
};

// ─── Material display names ─────────────────────────────────────────────────
const MAT_DISPLAY = {
  ZnO_TCO: 'ZnO:Al (TCO)', SnO2_FTO: 'SnO₂:F (FTO)', MgZnO: 'MgZnO',
  CdTe: 'CdTe (p-absorber)', CdTe_CSU: 'CdTe (CSU record)', CdTe_n: 'CdTe (n-emitter)',
  CdSeTe: 'CdSe₀.₄Te₀.₆', CdS: 'CdS', CIGS: 'CIGS',
  Perovskite: 'Perovskite (MAPbI₃)', Silicon: 'Silicon (p)',
  SnO2: 'SnO₂ (ETL)', Spiro_OMeTAD: 'Spiro-OMeTAD',
  Te: 'Te (back contact)',
  custom: 'Custom',
};

// ─── Material library (literature defaults per spec) ────────────────────────
const MAT_DEFAULTS = {
  ZnO_TCO: {
    category:'window', Eg:3.30, chi:4.50, eps_r:9.0,
    m_e_eff:0.24, m_h_eff:0.59, mu_e:100, mu_h:25,
    Nd:1e20, Na:0, tau_n_ns:1, tau_p_ns:1,
    thickness_um:0.20, B_rad:1e-11,
  },
  SnO2_FTO: {
    category:'window', Eg:3.60, chi:4.00, eps_r:9.0,
    m_e_eff:0.275, m_h_eff:0.900, mu_e:30, mu_h:8,
    Nd:1e20, Na:0, tau_n_ns:0.001, tau_p_ns:0.001,
    thickness_um:0.20, B_rad:1e-10,
  },
  MgZnO: {
    category:'buffer', Eg:3.62, chi:4.10, eps_r:9.0,
    m_e_eff:0.24, m_h_eff:0.59, mu_e:50, mu_h:25,
    Nd:5e16, Na:0, tau_n_ns:1, tau_p_ns:1,
    thickness_um:0.10, B_rad:1e-11,
  },
  CdTe: {
    category:'absorber', Eg:1.50, chi:4.28, eps_r:9.4,
    m_e_eff:0.096, m_h_eff:0.80, mu_e:100, mu_h:40,
    Nd:0, Na:1e15, tau_n_ns:100, tau_p_ns:10,
    thickness_um:2.0, B_rad:2e-10,
  },
  CdTe_CSU: {
    category:'absorber', Eg:1.50, chi:4.28, eps_r:9.4,
    m_e_eff:0.096, m_h_eff:0.80, mu_e:320, mu_h:40,
    Nd:0, Na:2e14, tau_n_ns:620, tau_p_ns:10,
    thickness_um:2.0, B_rad:2e-10,
  },
  CdTe_n: {
    category:'absorber', Eg:1.50, chi:4.28, eps_r:9.4,
    m_e_eff:0.096, m_h_eff:0.80, mu_e:320, mu_h:40,
    Nd:1e17, Na:0, tau_n_ns:1, tau_p_ns:1,
    thickness_um:0.20, B_rad:2e-10,
  },
  // v1.1.0 — CdSeTe electron affinity must be HIGHER than CdTe's (Se lowers
  // the gap mostly through the conduction band; kappa~0.7 [UNVERIFIED]), so a
  // CdTe layer behind a CdSeTe front acts as an electron reflector. Previous
  // 4.21 (< CdTe 4.28) had the sign inverted. chi = 4.28 + 0.7*(1.50-1.41).
  CdSeTe: {
    category:'absorber', Eg:1.41, chi:4.34, eps_r:9.7,
    m_e_eff:0.110, m_h_eff:0.65, mu_e:300, mu_h:40,
    Nd:0, Na:2e14, tau_n_ns:100, tau_p_ns:10,
    thickness_um:1.0, B_rad:2.5e-10,
  },
  CdS: {
    category:'buffer', Eg:2.42, chi:4.50, eps_r:10.0,
    m_e_eff:0.21, m_h_eff:0.80, mu_e:100, mu_h:25,
    Nd:1e17, Na:0, tau_n_ns:1, tau_p_ns:1,
    thickness_um:0.08, B_rad:1e-10,
  },
  CIGS: {
    category:'absorber', Eg:1.15, chi:4.40, eps_r:13.6,
    m_e_eff:0.09, m_h_eff:0.72, mu_e:100, mu_h:25,
    Nd:0, Na:2e16, tau_n_ns:50, tau_p_ns:10,
    thickness_um:2.0, B_rad:3e-11,
  },
  Perovskite: {
    category:'absorber', Eg:1.57, chi:3.90, eps_r:25.0,
    m_e_eff:0.19, m_h_eff:0.25, mu_e:10, mu_h:10,
    Nd:0, Na:1e16, tau_n_ns:100, tau_p_ns:100,
    thickness_um:0.50, B_rad:1e-10,
  },
  Silicon: {
    category:'absorber', Eg:1.12, chi:4.05, eps_r:11.7,
    m_e_eff:1.08, m_h_eff:0.57, mu_e:1400, mu_h:450,
    Nd:0, Na:1e16, tau_n_ns:1000, tau_p_ns:100,
    thickness_um:200.0, B_rad:1e-14,
  },
  SnO2: {
    category:'ETL', Eg:3.60, chi:4.00, eps_r:9.0,
    m_e_eff:0.30, m_h_eff:3.00, mu_e:240, mu_h:1,
    Nd:1e18, Na:0, tau_n_ns:1, tau_p_ns:1,
    thickness_um:0.04, B_rad:1e-11,
  },
  Spiro_OMeTAD: {
    category:'HTL', Eg:3.00, chi:2.45, eps_r:3.0,
    m_e_eff:1.00, m_h_eff:1.00, mu_e:2e-4, mu_h:2e-4,
    Nd:0, Na:2e17, tau_n_ns:1, tau_p_ns:1,
    thickness_um:0.20, B_rad:1e-11,
  },
  Te: {
    category:'contact', Eg:0.33, chi:4.84, eps_r:28.0,
    m_e_eff:0.14, m_h_eff:0.30, mu_e:100, mu_h:200,
    Nd:0, Na:1e17, tau_n_ns:1, tau_p_ns:1,
    thickness_um:0.02, B_rad:1e-10,
  },
  custom: {
    category:'custom', Eg:1.50, chi:4.00, eps_r:10.0,
    m_e_eff:0.10, m_h_eff:0.50, mu_e:100, mu_h:50,
    Nd:0, Na:1e15, tau_n_ns:100, tau_p_ns:10,
    thickness_um:1.0, B_rad:1e-10,
  },
};

// ─── Gradable properties ────────────────────────────────────────────────────
// Each layer property in this list can be edited as a uniform value or as
// a position-vs-value profile across the layer's thickness.
const GRADABLE_PROPS = ['Eg', 'chi', 'eps_r', 'Nd', 'Na', 'tau_n_ns', 'tau_p_ns',
                        'mu_e', 'mu_h'];

// ─── Physics helpers ────────────────────────────────────────────────────────
const kB = 8.617333e-5; // eV/K

function computeNc(m_e_eff, T = 300) {
  return 2.5e19 * Math.pow(m_e_eff, 1.5) * Math.pow(T / 300, 1.5);
}
function computeNv(m_h_eff, T = 300) {
  return 2.5e19 * Math.pow(m_h_eff, 1.5) * Math.pow(T / 300, 1.5);
}
function computeNi(Nc, Nv, Eg, T = 300) {
  return Math.sqrt(Nc * Nv) * Math.exp(-Eg / (2 * kB * T));
}

// ─── Formatting helpers ───────────────────────────────────────────────────
function formatSci(v) {
  if (v === undefined || v === null || isNaN(v)) return '';
  if (Math.abs(v) >= 1e4 || (Math.abs(v) < 1e-2 && v !== 0))
    return v.toExponential(2);
  return String(v);
}

function fmtThick(um) {
  if (um == null) return '';
  return um < 0.1 ? `${(um * 1000).toFixed(0)} nm` : `${um.toFixed(2)} μm`;
}

function genId() { return Math.random().toString(36).slice(2); }

// ─── SciField ───────────────────────────────────────────────────────────────
function SciField({ label, unit, value, onChange, readOnly, compact }) {
  const [local, setLocal] = useState('');
  const focused = useRef(false);

  useEffect(() => {
    if (!focused.current) setLocal(formatSci(value));
  }, [value]);

  return (
    <div className={compact ? 'dam-field dam-field-compact' : 'dam-field'}>
      <div className="dam-field-label">
        <span dangerouslySetInnerHTML={{ __html: label }} />
        {unit && <span className="dam-field-unit">{unit}</span>}
      </div>
      <input
        className={`dam-field-input ${readOnly ? 'readonly' : ''}`}
        type="text"
        value={local}
        onChange={e => {
          setLocal(e.target.value);
          const p = parseFloat(e.target.value);
          if (!isNaN(p) && onChange) onChange(p);
        }}
        onFocus={() => { focused.current = true; }}
        onBlur={() => { focused.current = false; setLocal(formatSci(value)); }}
        readOnly={readOnly}
      />
    </div>
  );
}

// ─── FreeNumInput ───────────────────────────────────────────────────────────
// Focus-guarded numeric input: keeps the raw text while the user types
// (so intermediate states like "1e1", "-0.", "" survive), commits only
// finite parses, and restores canonical formatting on blur. Fixes the
// "backspacing 1e14 collapses to 10" class of bugs.
function FreeNumInput({ value, onCommit, sci = false, className = 'dam-field-input',
                        min, max, ...rest }) {
  const [local, setLocal] = useState('');
  const focused = useRef(false);
  const fmt = v => (sci ? formatSci(v)
                        : (v === undefined || v === null || isNaN(v)) ? '' : String(v));

  useEffect(() => {
    if (!focused.current) setLocal(fmt(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, sci]);

  return (
    <input
      {...rest}
      className={className}
      type="text"
      inputMode="decimal"
      value={local}
      onChange={e => {
        setLocal(e.target.value);
        let p = parseFloat(e.target.value);
        if (!isNaN(p) && isFinite(p)) {
          if (min != null && p < min) p = min;
          if (max != null && p > max) p = max;
          if (onCommit) onCommit(p);
        }
      }}
      onFocus={() => { focused.current = true; }}
      onBlur={() => { focused.current = false; setLocal(fmt(value)); }}
    />
  );
}

// ─── NumField ───────────────────────────────────────────────────────────────
function NumField({ label, unit, value, onChange, step = 0.01, min, max, readOnly }) {
  return (
    <div className="dam-field">
      <div className="dam-field-label">
        <span dangerouslySetInnerHTML={{ __html: label }} />
        {unit && <span className="dam-field-unit">{unit}</span>}
      </div>
      <input
        className={`dam-field-input ${readOnly ? 'readonly' : ''}`}
        type="number"
        step={step} min={min} max={max}
        value={value ?? ''}
        onChange={e => onChange && onChange(parseFloat(e.target.value))}
        readOnly={readOnly}
      />
    </div>
  );
}

// (AutoManualNumField removed — gradable properties now use GradableField,
//  and composition-driven auto-fill of Eg/chi was retired with the bowing
//  formulas.)

// ─── TextField ──────────────────────────────────────────────────────────────
function TextField({ label, value, onChange }) {
  return (
    <div className="dam-field">
      <div className="dam-field-label"><span>{label}</span></div>
      <input className="dam-field-input" type="text" value={value}
        onChange={e => onChange(e.target.value)} />
    </div>
  );
}

// ─── Collapsible Section ────────────────────────────────────────────────────
function Collapsible({ title, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="dam-collapsible">
      <div className="dam-collapsible-header" onClick={() => setOpen(o => !o)}>
        <span>{title}</span>
        <span className="dam-collapsible-arrow">{open ? '▾' : '▸'}</span>
      </div>
      {open && <div className="dam-collapsible-body">{children}</div>}
    </div>
  );
}

// ─── Domain colors ──────────────────────────────────────────────────────────
const DOMAIN_COLORS = {
  substrate:      '#6b7280',
  frontContact:   '#3b82f6',
  frontInterface: '#22d3ee',
  absorber:       '#ef4444',
  backInterface:  '#22d3ee',
  backContact:    '#a855f7',
  metal:          '#f59e0b',
};

const DOMAIN_LABELS = {
  substrate:      'SUBSTRATE',
  frontContact:   'FRONT CONTACT',
  frontInterface: 'FRONT INTERFACE',
  absorber:       'ABSORBER',
  backInterface:  'BACK INTERFACE',
  backContact:    'BACK CONTACT',
};

// ─── Default interface state ────────────────────────────────────────────────
function defaultFrontInterface() {
  return { delta_Ec: null, delta_Ev: null, Sn: 1e3, Sp: 1e3, Qit: 0, traps: [] };
}
function defaultBackInterface() {
  return { delta_Ec: null, delta_Ev: null, Sn: 1e5, Sp: 1e5, Qit: 0, traps: [] };
}
function defaultContacts() {
  return { front_WF: 4.1, front_Sn: 1e7, front_Sp: 1e7, back_WF: 5.2, back_Sn: 1e7, back_Sp: 1e7 };
}
function defaultAvarodhaParams() {
  // Defensive scaffolding (per user spec). Field names chosen to match the
  // planned solver input the validation worktree's seed script documents.
  // `feff: null` signals "auto-compute on the backend"; user can override.
  // `disabled: false` keeps the analysis on by default for any device that
  // includes the field.
  return {
    Lg_nm: 2000,
    phi_GB_eV: 0.3,
    S_GB_cm_s: 1e5,
    feff: null,
    disabled: false,
  };
}

function defaultIllumination() {
  return { intensity_suns: 1.0, spectrum: 'AM1.5G', incident_side: 'front', temperature_K: 300 };
}

// ─── Clone layer for state init ─────────────────────────────────────────────
// Deep-copy one trap, including its optional graded-density profile.
function cloneTrap(t) {
  const out = { ...t };
  if (Array.isArray(t.Nt_profile)) out.Nt_profile = t.Nt_profile.map(n => ({ ...n }));
  return out;
}

function cloneLayer(l) {
  const out = {
    ...l,
    id: l.id || genId(),
    traps: l.traps ? l.traps.map(cloneTrap) : [],
    B_rad: l.B_rad ?? (MAT_DEFAULTS[l.material]?.B_rad ?? 1e-10),
  };
  // Deep-copy any per-property profile arrays
  GRADABLE_PROPS.forEach(p => {
    const key = `${p}_profile`;
    if (Array.isArray(l[key])) out[key] = l[key].map(n => ({ ...n }));
  });
  return out;
}

// ═════════════════════════════════════════════════════════════════════════════
// MAIN MODAL COMPONENT — Domain-Structured Device Architecture
// ═════════════════════════════════════════════════════════════════════════════
export default function DeviceArchitectureModal({
  isOpen,
  onClose,
  onApply,
  device: initialDevice,
  onSavePreset,
  customPresets = [],
}) {
  // ── Domain state ──────────────────────────────────────
  const [substrate, setSubstrate] = useState({ material: 'Glass', thickness_um: 1000 });
  const [fcLayers, setFcLayers] = useState([]);
  const [frontIface, setFrontIface] = useState(defaultFrontInterface());
  const [absLayers, setAbsLayers] = useState([]);
  const [backIface, setBackIface] = useState(defaultBackInterface());
  const [bcLayers, setBcLayers] = useState([]);
  const [contacts, setContacts] = useState(defaultContacts());
  const [illumination, setIllumination] = useState(defaultIllumination());
  // Avarodha diagnostic params — see defaultAvarodhaParams() docstring.
  const [avarodha, setAvarodha] = useState(defaultAvarodhaParams());

  // ── UI state ──────────────────────────────────────────
  const [section, setSection] = useState('absorber');
  const [selLayerId, setSelLayerId] = useState(null);
  const [addMatId, setAddMatId] = useState('CdTe');
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [savePresetName, setSavePresetName] = useState('');
  const [overwriteConfirm, setOverwriteConfirm] = useState(false);

  // ── Initialize from props ─────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    const d = initialDevice || {};
    setSubstrate(d.substrate || { material: 'Glass', thickness_um: 1000 });
    const fc = (d.frontContact?.layers || []).map(cloneLayer);
    setFcLayers(fc);
    setFrontIface({ ...defaultFrontInterface(), ...(d.frontInterface || {}) });
    const abs = (d.absorber?.layers || []).map(cloneLayer);
    setAbsLayers(abs);
    setBackIface({ ...defaultBackInterface(), ...(d.backInterface || {}) });
    const bc = (d.backContact?.layers || []).map(cloneLayer);
    setBcLayers(bc);
    setContacts({ ...defaultContacts(), ...(d.contacts || {}) });
    setIllumination({ ...defaultIllumination(), ...(d.illumination || {}) });
    setAvarodha({ ...defaultAvarodhaParams(), ...(d.avarodha_params || {}) });

    setSection('absorber');
    setSelLayerId(abs.length > 0 ? abs[0].id : null);
    setSaveDialogOpen(false);
    setSavePresetName('');
    setOverwriteConfirm(false);
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Escape key ────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  // ── Stage 2: publish to WorkspaceContext (device_modal block) ────
  // While open, publishes a thin slice of the modal's working state.
  // When closed, clears the slot via publish('device_modal', null). The
  // provider treats device_modal != null as "modal is active" and flips
  // active_tab → 'device_modal' for the chat.
  // Full working_device (per §3.3) is not published in Stage 2; Stage 5
  // adds it when AI-driven device construction lands.
  const publishCtx = useContext(WorkspaceContextSetter);
  const fcCount = fcLayers.length;
  const absCount = absLayers.length;
  const bcCount = bcLayers.length;
  const selectedLayerName = (() => {
    const ls = section === 'frontContact' ? fcLayers
             : section === 'absorber' ? absLayers
             : section === 'backContact' ? bcLayers : [];
    return ls.find(l => l.id === selLayerId)?.name || null;
  })();
  useEffect(() => {
    if (!isOpen) {
      publishCtx('device_modal', null);
      return;
    }
    publishCtx('device_modal', {
      open: true,
      section,
      selected_layer_id: selLayerId,
      selected_layer_name: selectedLayerName,
      save_dialog_open: saveDialogOpen,
      working_layer_counts: {
        front_contact: fcCount,
        absorber: absCount,
        back_contact: bcCount,
      },
      working_device: null,   // TODO Stage 5: project full working device
      dirty: null,            // TODO Stage 5: deep-compare to initialDevice
    });
  }, [publishCtx, isOpen, section, selLayerId, selectedLayerName,
      saveDialogOpen, fcCount, absCount, bcCount]);

  // On unmount, always clear the slot — e.g. if the modal is unmounted
  // by parent (not just closed via onClose).
  useEffect(() => {
    return () => publishCtx('device_modal', null);
  }, [publishCtx]);

  if (!isOpen) return null;

  // ── Domain layer accessors ────────────────────────────
  function domainLayers(sec) {
    if (sec === 'frontContact') return fcLayers;
    if (sec === 'absorber') return absLayers;
    if (sec === 'backContact') return bcLayers;
    return [];
  }
  function setDomainLayers(sec, updater) {
    const setter = sec === 'frontContact' ? setFcLayers
      : sec === 'absorber' ? setAbsLayers
      : sec === 'backContact' ? setBcLayers : null;
    if (setter) setter(typeof updater === 'function' ? updater : () => updater);
  }

  const isDomainSection = section === 'frontContact' || section === 'absorber' || section === 'backContact';
  const currentLayers = domainLayers(section);
  const selectedLayer = currentLayers.find(l => l.id === selLayerId);

  // ── Layer operations ──────────────────────────────────
  function selectDomain(sec) {
    setSection(sec);
    const ls = domainLayers(sec);
    setSelLayerId(ls.length > 0 ? ls[0].id : null);
  }

  function addLayer() {
    const d = MAT_DEFAULTS[addMatId] || MAT_DEFAULTS.custom;
    const newL = {
      id: genId(), material: addMatId, name: MAT_DISPLAY[addMatId] || addMatId,
      ...d, traps: [],
    };
    setDomainLayers(section, ls => [...ls, newL]);
    setSelLayerId(newL.id);
  }

  function removeLayer(id) {
    setDomainLayers(section, ls => {
      const f = ls.filter(l => l.id !== id);
      if (selLayerId === id) setSelLayerId(f.length > 0 ? f[0].id : null);
      return f;
    });
  }

  function moveLayer(id, dir) {
    setDomainLayers(section, ls => {
      const i = ls.findIndex(l => l.id === id);
      const j = i + dir;
      if (j < 0 || j >= ls.length) return ls;
      const out = [...ls];
      [out[i], out[j]] = [out[j], out[i]];
      return out;
    });
  }

  function updateLayer(id, updates) {
    setDomainLayers(section, ls => ls.map(l => l.id === id ? { ...l, ...updates } : l));
  }
  function updateSelected(updates) {
    if (selLayerId) updateLayer(selLayerId, updates);
  }

  function autoPopulate() {
    if (!selectedLayer) return;
    const d = MAT_DEFAULTS[selectedLayer.material];
    // Fill from library defaults resets any manual Eg/chi overrides too.
    if (d) updateSelected({
      ...d, name: selectedLayer.name, traps: selectedLayer.traps,
      _manual: undefined,
    });
  }

  // ── Trap operations ───────────────────────────────────
  function addTrap() {
    if (!selectedLayer) return;
    const t = { id: genId(), type: 'acceptor', Et_ref: 'Ev', Et: 0.0, sigma_n: 1e-15, sigma_p: 1e-15, Nt: 1e14 };
    updateSelected({ traps: [...(selectedLayer.traps || []), t] });
  }
  function updateTrap(tid, u) {
    if (!selectedLayer) return;
    updateSelected({ traps: (selectedLayer.traps || []).map(t => t.id === tid ? { ...t, ...u } : t) });
  }
  function removeTrap(tid) {
    if (!selectedLayer) return;
    updateSelected({ traps: (selectedLayer.traps || []).filter(t => t.id !== tid) });
  }

  // ── Per-property grading operations (bound to the selected layer) ──
  // A property is "graded" when its profile array on the layer has >=2
  // nodes. `setUniform` clears the profile (uniform mode); `setGraded`
  // seeds a 2-node profile spanning the layer thickness.
  function setUniform(prop) {
    if (!selectedLayer) return;
    updateSelected({ [`${prop}_profile`]: undefined });
  }
  function setGraded(prop) {
    if (!selectedLayer) return;
    const thick_nm = (selectedLayer.thickness_um || 1) * 1000;
    const v = selectedLayer[prop];
    updateSelected({
      [`${prop}_profile`]: [
        { id: genId(), position_nm: 0,        value: v },
        { id: genId(), position_nm: thick_nm, value: v },
      ],
    });
  }
  function setProfile(prop, profile) {
    if (!selectedLayer) return;
    updateSelected({ [`${prop}_profile`]: profile });
  }

  // ── Apply ─────────────────────────────────────────────
  function cleanLayers(layers) {
    return layers.map(l => {
      const out = { ...l };
      if (!out.traps || out.traps.length === 0) {
        delete out.traps;
      } else {
        // Drop empty / single-node trap-density profiles
        out.traps = out.traps.map(t => {
          const c = { ...t };
          if (!Array.isArray(c.Nt_profile) || c.Nt_profile.length < 2) {
            delete c.Nt_profile;
          }
          return c;
        });
      }
      // Drop empty / single-node profiles
      GRADABLE_PROPS.forEach(p => {
        const key = `${p}_profile`;
        if (!Array.isArray(out[key]) || out[key].length < 2) delete out[key];
      });
      delete out._manual;
      return out;
    });
  }

  function handleApply() {
    onApply({
      substrate,
      frontContact: { layers: cleanLayers(fcLayers) },
      frontInterface: { ...frontIface },
      absorber: { layers: cleanLayers(absLayers) },
      backInterface: { ...backIface },
      backContact: { layers: cleanLayers(bcLayers) },
      contacts,
      illumination,
      avarodha_params: { ...avarodha },
    });
  }

  // ── Save as Preset ────────────────────────────────────
  function handleSavePreset() {
    const name = savePresetName.trim();
    const allLayers = [...fcLayers, ...absLayers, ...bcLayers];
    if (!name || allLayers.length === 0) return;
    const existing = customPresets.find(p => p.name === name);
    if (existing && !overwriteConfirm) { setOverwriteConfirm(true); return; }
    onSavePreset({
      name,
      overwriteId: existing?.id || null,
      device: {
        substrate,
        frontContact: { layers: cleanLayers(fcLayers) },
        frontInterface: { ...frontIface },
        absorber: { layers: cleanLayers(absLayers) },
        backInterface: { ...backIface },
        backContact: { layers: cleanLayers(bcLayers) },
        contacts,
        illumination,
        avarodha_params: { ...avarodha },
      },
    });
    setSaveDialogOpen(false);
    setSavePresetName('');
    setOverwriteConfirm(false);
  }

  // ── Computed ──────────────────────────────────────────
  const Nc = selectedLayer ? computeNc(selectedLayer.m_e_eff || 0.1) : 0;
  const Nv = selectedLayer ? computeNv(selectedLayer.m_h_eff || 0.5) : 0;
  const ni = selectedLayer ? computeNi(Nc, Nv, selectedLayer.Eg || 1.5) : 0;

  // Total thickness summary
  const allLayers = [...fcLayers, ...absLayers, ...bcLayers];
  const totalUm = allLayers.reduce((s, l) => s + (l.thickness_um || 0), 0);

  // Summary string. (l.name || '') guards against layers that arrived
  // without a populated display name — happens when the AI device-spec
  // path emits a layer with only `material` set (see specToDevice in
  // App.js, fixed in the same commit). Without the guard, .split() on
  // undefined crashes the modal on open.
  const fcSummary = fcLayers.map(l => (l.name || l.material || '').split(' ')[0]).join('/');
  const absSummary = absLayers.map(l => (l.name || l.material || '').split(' ')[0]).join('/');
  const bcSummary = bcLayers.map(l => (l.name || l.material || '').split(' ')[0]).join('/');

  return (
    <div className="dam-overlay" onClick={e => e.stopPropagation()}>
      <div className="dam-modal dam-modal-fullscreen">
        {/* ── Title Bar ──────────────────────────────────── */}
        <div className="dam-titlebar">
          <div className="dam-title">DEVICE ARCHITECTURE</div>
          <div className="dam-title-meta">
            {allLayers.length} layers &middot; {totalUm.toFixed(2)} &mu;m total
            {fcSummary && ` | ${fcSummary}`}
            {absSummary && ` | ${absSummary}`}
            {bcSummary && ` | ${bcSummary}`}
          </div>
          <button className="dam-close-btn" onClick={onClose} title="Close (Esc)">&times;</button>
        </div>

        {/* ── Two-Column Body ────────────────────────────── */}
        <div className="dam-body dam-body-two-col">

          {/* ═══ LEFT: Device Stack Overview ═══ */}
          <div className="dam-col dam-col-stack">
            <div className="dam-col-header">Device Stack</div>

            {/* Illumination indicator */}
            <div className="dam-domain-bar" style={{ borderLeftColor: '#fbbf24', background: 'rgba(251,191,36,0.06)' }}>
              <span style={{ color: '#fbbf24' }}>&#9788; ILLUMINATION</span>
              <span style={{ fontSize: 10, color: 'var(--text-2)' }}>
                {illumination.incident_side === 'both' ? '↕ both'
                  : illumination.incident_side === 'rear' ? '↑ rear' : '↓ front'}
              </span>
            </div>

            {/* Substrate */}
            <div className={`dam-domain-section ${section === 'substrate' ? 'selected' : ''}`}
              style={{ borderLeftColor: DOMAIN_COLORS.substrate }}
              onClick={() => setSection('substrate')}>
              <div className="dam-domain-name">SUBSTRATE</div>
              <div className="dam-domain-detail">{substrate.material} {substrate.thickness_um} μm</div>
            </div>

            {/* Front Contact */}
            <div className={`dam-domain-section ${section === 'frontContact' ? 'selected' : ''}`}
              style={{ borderLeftColor: DOMAIN_COLORS.frontContact }}
              onClick={() => selectDomain('frontContact')}>
              <div className="dam-domain-name" style={{ color: DOMAIN_COLORS.frontContact }}>FRONT CONTACT</div>
              {fcLayers.map(l => (
                <div key={l.id} className={`dam-domain-layer ${selLayerId === l.id && section === 'frontContact' ? 'active' : ''}`}
                  onClick={e => { e.stopPropagation(); setSection('frontContact'); setSelLayerId(l.id); }}>
                  {l.name} &mdash; {fmtThick(l.thickness_um)}
                </div>
              ))}
              {fcLayers.length === 0 && <div className="dam-domain-empty">No layers (direct contact)</div>}
            </div>

            {/* Front Interface */}
            <div className={`dam-iface-divider ${section === 'frontInterface' ? 'selected' : ''}`}
              onClick={() => setSection('frontInterface')}>
              <span>&#9552;&#9552; FRONT INTERFACE &#9552;&#9552;</span>
              <span className="dam-iface-summary">
                &#916;Ec={frontIface.delta_Ec != null ? frontIface.delta_Ec.toFixed(2) : 'auto'}
                {' '}Sn={formatSci(frontIface.Sn)}
              </span>
            </div>

            {/* Absorber */}
            <div className={`dam-domain-section ${section === 'absorber' ? 'selected' : ''}`}
              style={{ borderLeftColor: DOMAIN_COLORS.absorber }}>
              <div className="dam-domain-name" style={{ color: DOMAIN_COLORS.absorber }}
                onClick={() => selectDomain('absorber')}>ABSORBER</div>
              {absLayers.map(l => (
                <div key={l.id} className={`dam-domain-layer ${selLayerId === l.id && section === 'absorber' ? 'active' : ''}`}
                  onClick={() => { setSection('absorber'); setSelLayerId(l.id); }}>
                  {l.name} &mdash; {l.Nd > l.Na ? 'n' : 'p'} &mdash; {fmtThick(l.thickness_um)}
                  {GRADABLE_PROPS.some(p => Array.isArray(l[`${p}_profile`]) && l[`${p}_profile`].length >= 2) && (
                    <span className="dam-graded-badge">GRADED</span>
                  )}
                </div>
              ))}
              {absLayers.length === 0 && <div className="dam-domain-empty">No absorber layers</div>}
            </div>

            {/* Back Interface */}
            <div className={`dam-iface-divider ${section === 'backInterface' ? 'selected' : ''}`}
              onClick={() => setSection('backInterface')}>
              <span>&#9552;&#9552; BACK INTERFACE &#9552;&#9552;</span>
              <span className="dam-iface-summary">
                &#916;Ec={backIface.delta_Ec != null ? backIface.delta_Ec.toFixed(2) : 'auto'}
                {' '}Sn={formatSci(backIface.Sn)}
              </span>
            </div>

            {/* Back Contact */}
            <div className={`dam-domain-section ${section === 'backContact' ? 'selected' : ''}`}
              style={{ borderLeftColor: DOMAIN_COLORS.backContact }}
              onClick={() => selectDomain('backContact')}>
              <div className="dam-domain-name" style={{ color: DOMAIN_COLORS.backContact }}>BACK CONTACT</div>
              {bcLayers.map(l => (
                <div key={l.id} className={`dam-domain-layer ${selLayerId === l.id && section === 'backContact' ? 'active' : ''}`}
                  onClick={e => { e.stopPropagation(); setSection('backContact'); setSelLayerId(l.id); }}>
                  {l.name} &mdash; {fmtThick(l.thickness_um)}
                </div>
              ))}
              {bcLayers.length === 0 && <div className="dam-domain-empty">No layers (direct contact)</div>}
            </div>

            {/* Metal Electrode */}
            <div className="dam-domain-bar" style={{ borderLeftColor: '#f59e0b', background: 'rgba(245,158,11,0.06)' }}>
              <span style={{ color: '#f59e0b' }}>METAL ELECTRODE</span>
            </div>

            {/* Avarodha diagnostic parameters — appears in the left nav as a
                clickable item. Selecting it switches the right editor to
                the Avarodha form. Field values live in `avarodha` state and
                ship as device.avarodha_params on Apply. Backend currently
                ignores the field (extra="ignore") — see commit log re:
                defensive scaffolding for the unfinished solver feature. */}
            <div className={`dam-domain-section ${section === 'avarodha' ? 'selected' : ''}`}
              style={{ borderLeftColor: '#a855f7', marginTop: 8 }}
              onClick={() => setSection('avarodha')}>
              <div className="dam-domain-name" style={{ color: '#a855f7' }}>AVARODHA PARAMETERS</div>
              <div className="dam-domain-empty" style={{ fontSize: 11 }}>
                {avarodha.disabled ? 'analysis disabled' : `Lg=${avarodha.Lg_nm}nm · S_GB=${formatSci(avarodha.S_GB_cm_s)}`}
              </div>
            </div>

            {/* Contacts Fallback */}
            <Collapsible title="Fallback Contact BCs" defaultOpen={false}>
              <div style={{ padding: '4px 0' }}>
                <div className="dam-hint" style={{ marginBottom: 8 }}>
                  Used when no contact layers are defined in a domain.
                </div>
                <div className="dam-subsection-label">Front</div>
                <NumField label="&Phi;<sub>m</sub>" unit="eV" value={contacts.front_WF}
                  onChange={v => setContacts(c => ({ ...c, front_WF: v }))} step={0.05} min={2} max={7} />
                <div className="dam-field-row">
                  <SciField label="S<sub>n</sub>" unit="cm/s" value={contacts.front_Sn}
                    onChange={v => setContacts(c => ({ ...c, front_Sn: v }))} compact />
                  <SciField label="S<sub>p</sub>" unit="cm/s" value={contacts.front_Sp}
                    onChange={v => setContacts(c => ({ ...c, front_Sp: v }))} compact />
                </div>
                <div className="dam-subsection-label" style={{ marginTop: 8 }}>Back</div>
                <NumField label="&Phi;<sub>m</sub>" unit="eV" value={contacts.back_WF}
                  onChange={v => setContacts(c => ({ ...c, back_WF: v }))} step={0.05} min={2} max={7} />
                <div className="dam-field-row">
                  <SciField label="S<sub>n</sub>" unit="cm/s" value={contacts.back_Sn}
                    onChange={v => setContacts(c => ({ ...c, back_Sn: v }))} compact />
                  <SciField label="S<sub>p</sub>" unit="cm/s" value={contacts.back_Sp}
                    onChange={v => setContacts(c => ({ ...c, back_Sp: v }))} compact />
                </div>
              </div>
            </Collapsible>
          </div>

          {/* ═══ RIGHT: Domain/Interface Editor ═══ */}
          <div className="dam-col dam-col-editor">

            {/* ── SUBSTRATE EDITOR ──────────────────────── */}
            {section === 'substrate' && (
              <div className="dam-editor">
                <div className="dam-editor-header">
                  <div className="dam-editor-title">Substrate</div>
                </div>
                <div className="dam-editor-body" style={{ padding: 16 }}>
                  <div className="dam-hint" style={{ marginBottom: 12 }}>
                    Optical only &mdash; no electrical transport in substrate.
                  </div>
                  <div className="dam-field">
                    <div className="dam-field-label"><span>Material</span></div>
                    <select className="dam-field-input" value={substrate.material}
                      onChange={e => setSubstrate(s => ({ ...s, material: e.target.value }))}>
                      <option value="Glass">Glass</option>
                      <option value="PET">PET (flexible)</option>
                      <option value="Steel">Steel</option>
                      <option value="None">None (superstrate)</option>
                    </select>
                  </div>
                  <NumField label="Thickness" unit="μm" value={substrate.thickness_um}
                    onChange={v => setSubstrate(s => ({ ...s, thickness_um: v }))}
                    step={100} min={0} max={10000} />
                </div>
              </div>
            )}

            {/* ── DOMAIN LAYER EDITOR ───────────────────── */}
            {isDomainSection && (
              <>
                {/* Header with add layer */}
                <div className="dam-editor-header">
                  <div className="dam-editor-title">{DOMAIN_LABELS[section]} Layers</div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <select className="dam-field-input" value={addMatId}
                      onChange={e => setAddMatId(e.target.value)} style={{ width: 160 }}>
                      {Object.entries(MATERIAL_CATEGORIES).map(([cat, mats]) => (
                        <optgroup key={cat} label={cat.toUpperCase()}>
                          {mats.map(m => <option key={m} value={m}>{MAT_DISPLAY[m] || m}</option>)}
                        </optgroup>
                      ))}
                    </select>
                    <button className="dam-btn dam-btn-accent" onClick={addLayer}>+ Add Layer</button>
                  </div>
                </div>

                {/* Layer cards (compact, scrollable) */}
                <div className="dam-layer-list" style={{ maxHeight: 140 }}>
                  {currentLayers.map((layer, idx) => {
                    const cat = (MAT_DEFAULTS[layer.material]?.category) || 'custom';
                    const isSel = selLayerId === layer.id;
                    return (
                      <div key={layer.id}
                        className={`dam-layer-card ${isSel ? 'selected' : ''}`}
                        onClick={() => setSelLayerId(layer.id)}>
                        <div className="dam-layer-swatch" style={{ background: layerColor(cat) }} />
                        <div className="dam-layer-info">
                          <div className="dam-layer-name">
                            {layer.name}
                            {GRADABLE_PROPS.some(p => Array.isArray(layer[`${p}_profile`]) && layer[`${p}_profile`].length >= 2) && (
                              <span className="dam-graded-badge">GRADED</span>
                            )}
                          </div>
                          <div className="dam-layer-meta">
                            {fmtThick(layer.thickness_um)}
                            {' · '}{layer.Nd > layer.Na ? 'n' : 'p'}-type
                            {' · '}Eg={layer.Eg?.toFixed(2)}
                          </div>
                        </div>
                        <div className="dam-layer-actions">
                          <button className="dam-action-btn" title="Move up"
                            onClick={e => { e.stopPropagation(); moveLayer(layer.id, -1); }}
                            disabled={idx === 0}>&#9650;</button>
                          <button className="dam-action-btn" title="Move down"
                            onClick={e => { e.stopPropagation(); moveLayer(layer.id, 1); }}
                            disabled={idx === currentLayers.length - 1}>&#9660;</button>
                          <button className="dam-action-btn danger" title="Delete layer"
                            onClick={e => { e.stopPropagation(); removeLayer(layer.id); }}>&times;</button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Layer parameter editor */}
                {selectedLayer ? (
                  <LayerParameterEditor
                    layer={selectedLayer}
                    layerIndex={currentLayers.findIndex(l => l.id === selLayerId)}
                    totalLayers={currentLayers.length}
                    onChange={updateSelected}
                    onAutoPopulate={autoPopulate}
                    Nc={Nc} Nv={Nv} ni={ni}
                    onAddTrap={addTrap}
                    onUpdateTrap={updateTrap}
                    onRemoveTrap={removeTrap}
                    onSetUniform={setUniform}
                    onSetGraded={setGraded}
                    onSetProfile={setProfile}
                  />
                ) : (
                  <div className="dam-empty">
                    <div className="dam-empty-text">Add a layer to get started</div>
                  </div>
                )}

                {/* Derived BC panel for contact domains */}
                {(section === 'frontContact' || section === 'backContact') && currentLayers.length > 0 && (
                  <DerivedBCPanel
                    layers={currentLayers}
                    side={section === 'frontContact' ? 'front' : 'back'}
                    contacts={contacts}
                    onContactsChange={setContacts}
                  />
                )}
              </>
            )}

            {/* ── INTERFACE EDITOR ──────────────────────── */}
            {(section === 'frontInterface' || section === 'backInterface') && (
              <InterfaceEditor
                iface={section === 'frontInterface' ? frontIface : backIface}
                onChange={section === 'frontInterface' ? setFrontIface : setBackIface}
                name={section === 'frontInterface' ? 'Front Interface' : 'Back Interface'}
                leftLayers={section === 'frontInterface' ? fcLayers : absLayers}
                rightLayers={section === 'frontInterface' ? absLayers : bcLayers}
              />
            )}

            {/* ── AVARODHA PARAMETERS EDITOR ──────────────────────────────
                Defensive scaffolding for the planned _compute_avarodha_outputs
                solver feature. Inputs serialize as device.avarodha_params and
                are silently ignored by the backend until the solver lands. */}
            {section === 'avarodha' && (
              <div className="dam-editor">
                <div className="dam-editor-header">
                  <div className="dam-editor-title" style={{ color: '#a855f7' }}>
                    Avarodha Parameters
                  </div>
                </div>
                <div className="dam-editor-body" style={{ padding: 16 }}>
                  <div className="dam-hint" style={{ marginBottom: 12 }}>
                    Configures the polycrystalline grain-boundary recombination
                    subnetwork for the Avarodha analysis. Disable to skip the
                    analysis entirely. <strong>Status:</strong> the solver's
                    <code> _compute_avarodha_outputs</code> implementation is
                    planned but not yet shipped — these inputs are accepted and
                    serialized, but the diagnostic outputs will only appear in
                    the results panel once the solver computes them.
                  </div>
                  <NumField
                    label="Grain size L<sub>g</sub>"
                    unit="nm"
                    value={avarodha.Lg_nm}
                    onChange={v => setAvarodha(a => ({ ...a, Lg_nm: v }))}
                    step={100} min={10} max={50000}
                  />
                  <NumField
                    label="GB barrier &phi;<sub>GB</sub>"
                    unit="eV"
                    value={avarodha.phi_GB_eV}
                    onChange={v => setAvarodha(a => ({ ...a, phi_GB_eV: v }))}
                    step={0.05} min={0} max={2}
                  />
                  <SciField
                    label="GB recomb. velocity S<sub>GB</sub>"
                    unit="cm/s"
                    value={avarodha.S_GB_cm_s}
                    onChange={v => setAvarodha(a => ({ ...a, S_GB_cm_s: v }))}
                  />
                  <div className="dam-field">
                    <div className="dam-field-label">
                      <span>Participation fraction f<sub>eff</sub></span>
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input
                        className="dam-field-input"
                        type="text"
                        placeholder="(auto)"
                        value={avarodha.feff == null ? '' : avarodha.feff}
                        onChange={e => {
                          const raw = e.target.value.trim();
                          const parsed = raw === '' ? null : parseFloat(raw);
                          setAvarodha(a => ({
                            ...a,
                            feff: (raw === '' || Number.isNaN(parsed)) ? null : parsed,
                          }));
                        }}
                        style={{ flex: 1 }}
                      />
                      <button
                        type="button"
                        onClick={() => setAvarodha(a => ({ ...a, feff: null }))}
                        title="Clear → backend auto-computes from Lg + wGB"
                        style={{
                          padding: '4px 10px', fontSize: 11,
                          background: 'transparent', border: '1px solid var(--border, #30363d)',
                          color: 'var(--text-muted, #8b949e)', cursor: 'pointer', borderRadius: 4,
                        }}
                      >
                        Auto
                      </button>
                    </div>
                    <div className="dam-hint" style={{ marginTop: 4, fontSize: 10 }}>
                      Leave blank or click Auto for backend auto-compute from L<sub>g</sub> and the GB-layer width.
                    </div>
                  </div>
                  <div className="dam-field" style={{ marginTop: 12 }}>
                    <label style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      cursor: 'pointer', fontSize: 12,
                    }}>
                      <input
                        type="checkbox"
                        checked={!!avarodha.disabled}
                        onChange={e => setAvarodha(a => ({ ...a, disabled: e.target.checked }))}
                      />
                      <span>Disable Avarodha analysis for this device</span>
                    </label>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Bottom Bar ─────────────────────────────────── */}
        <div className="dam-bottombar">
          <button className="dam-btn dam-btn-ghost" onClick={onClose}>Cancel</button>
          <div className="dam-bottombar-right">
            {!saveDialogOpen ? (
              <button className="dam-btn dam-btn-secondary" onClick={() => setSaveDialogOpen(true)}>
                Save as Preset
              </button>
            ) : overwriteConfirm ? (
              <div className="dam-save-inline">
                <span style={{ fontSize: 11, color: 'var(--amber)' }}>
                  Preset "{savePresetName}" exists. Overwrite?
                </span>
                <button className="dam-btn dam-btn-accent dam-btn-sm" onClick={handleSavePreset}>Yes</button>
                <button className="dam-btn dam-btn-ghost dam-btn-sm"
                  onClick={() => setOverwriteConfirm(false)}>No</button>
              </div>
            ) : (
              <div className="dam-save-inline">
                <input className="dam-field-input" type="text" placeholder="Preset name..."
                  value={savePresetName} onChange={e => { setSavePresetName(e.target.value); setOverwriteConfirm(false); }}
                  onKeyDown={e => { if (e.key === 'Enter') handleSavePreset(); if (e.key === 'Escape') setSaveDialogOpen(false); }}
                  autoFocus style={{ width: 180 }} />
                <button className="dam-btn dam-btn-secondary" onClick={handleSavePreset}
                  disabled={!savePresetName.trim()}>Save</button>
                <button className="dam-btn dam-btn-ghost" onClick={() => { setSaveDialogOpen(false); setOverwriteConfirm(false); }}>Cancel</button>
              </div>
            )}
            <button className="dam-btn dam-btn-primary" onClick={handleApply}>Apply</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// INTERFACE EDITOR
// ═════════════════════════════════════════════════════════════════════════════
function InterfaceEditor({ iface, onChange, name, leftLayers, rightLayers }) {
  const set = (key, val) => onChange({ ...iface, [key]: val });

  // Compute auto band offsets from chi values of adjacent layers
  const leftLast = leftLayers[leftLayers.length - 1];
  const rightFirst = rightLayers[0];
  const autoEc = (leftLast && rightFirst) ? (leftLast.chi - rightFirst.chi) : null;
  const autoEv = (leftLast && rightFirst)
    ? ((rightFirst.chi + rightFirst.Eg) - (leftLast.chi + leftLast.Eg)) : null;

  const ecIsAuto = iface.delta_Ec === null || iface.delta_Ec === undefined;
  const evIsAuto = iface.delta_Ev === null || iface.delta_Ev === undefined;

  function updateIfaceTrap(tid, updates) {
    set('traps', (iface.traps || []).map(t => t.id === tid ? { ...t, ...updates } : t));
  }

  return (
    <div className="dam-editor">
      <div className="dam-editor-header">
        <div className="dam-editor-title" style={{ color: '#22d3ee' }}>{name}</div>
        {leftLast && rightFirst && (
          <div style={{ fontSize: 11, color: 'var(--text-2)', fontFamily: 'var(--mono)' }}>
            {leftLast.name} / {rightFirst.name}
          </div>
        )}
      </div>
      <div className="dam-editor-body" style={{ padding: 16 }}>

        {/* Band Alignment */}
        <div className="dam-subsection-label">Band Alignment</div>

        <div className="dam-field">
          <div className="dam-field-label">
            <span dangerouslySetInnerHTML={{ __html: '&Delta;E<sub>c</sub>' }} />
            <span className="dam-field-unit">eV</span>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <label style={{ fontSize: 11, color: 'var(--text-2)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
              <input type="checkbox" checked={ecIsAuto}
                onChange={e => set('delta_Ec', e.target.checked ? null : (autoEc ?? 0))} />
              {' '}Auto
            </label>
            <input className={`dam-field-input ${ecIsAuto ? 'readonly' : ''}`}
              type="text" style={{ flex: 1 }}
              value={ecIsAuto ? (autoEc != null ? autoEc.toFixed(3) : '—') : iface.delta_Ec}
              readOnly={ecIsAuto}
              onChange={e => { const v = parseFloat(e.target.value); if (!isNaN(v)) set('delta_Ec', v); }} />
          </div>
          {ecIsAuto && autoEc != null && (
            <div className="dam-hint" style={{ marginTop: 2 }}>
              Auto: computed from &chi; values = {autoEc.toFixed(3)} eV
            </div>
          )}
        </div>

        <div className="dam-field">
          <div className="dam-field-label">
            <span dangerouslySetInnerHTML={{ __html: '&Delta;E<sub>v</sub>' }} />
            <span className="dam-field-unit">eV</span>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <label style={{ fontSize: 11, color: 'var(--text-2)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
              <input type="checkbox" checked={evIsAuto}
                onChange={e => set('delta_Ev', e.target.checked ? null : (autoEv ?? 0))} />
              {' '}Auto
            </label>
            <input className={`dam-field-input ${evIsAuto ? 'readonly' : ''}`}
              type="text" style={{ flex: 1 }}
              value={evIsAuto ? (autoEv != null ? autoEv.toFixed(3) : '—') : iface.delta_Ev}
              readOnly={evIsAuto}
              onChange={e => { const v = parseFloat(e.target.value); if (!isNaN(v)) set('delta_Ev', v); }} />
          </div>
          {evIsAuto && autoEv != null && (
            <div className="dam-hint" style={{ marginTop: 2 }}>
              Auto: computed from &chi; + Eg = {autoEv.toFixed(3)} eV
            </div>
          )}
        </div>

        <div className="dam-divider" />
        <div className="dam-subsection-label">Interface Recombination</div>

        <SciField label="S<sub>n</sub> (electrons)" unit="cm/s" value={iface.Sn}
          onChange={v => set('Sn', v)} />
        <SciField label="S<sub>p</sub> (holes)" unit="cm/s" value={iface.Sp}
          onChange={v => set('Sp', v)} />
        <SciField label="Q<sub>it</sub> (fixed charge)" unit="cm⁻²" value={iface.Qit}
          onChange={v => set('Qit', v)} />

        <div className="dam-divider" />
        <div className="dam-subsection-label">Interface Trap Levels</div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <span style={{ fontSize: 11, color: 'var(--text-2)' }}>
            {(iface.traps || []).length} trap level{(iface.traps || []).length !== 1 ? 's' : ''}
          </span>
          <button className="dam-btn dam-btn-accent dam-btn-sm" onClick={() => {
            const t = { id: genId(), type: 'donor', Et_eV: 0.75, sigma_n: 1e-14, sigma_p: 1e-14, Nit: 1e11, reference: 'Ev' };
            set('traps', [...(iface.traps || []), t]);
          }}>+ Add Trap Level</button>
        </div>

        {(iface.traps || []).length === 0 && (
          <div className="dam-muted">No interface traps defined</div>
        )}

        {(iface.traps || []).map((trap, i) => (
          <div key={trap.id} className="dam-trap-card">
            <div className="dam-trap-header">
              <span>Trap {i + 1}</span>
              <button className="dam-action-btn danger"
                onClick={() => set('traps', (iface.traps || []).filter(t => t.id !== trap.id))}>&times;</button>
            </div>
            <div className="dam-field">
              <div className="dam-field-label"><span>Type</span></div>
              <select className="dam-field-input" value={trap.type}
                onChange={e => updateIfaceTrap(trap.id, { type: e.target.value })}>
                <option value="donor">Donor</option>
                <option value="acceptor">Acceptor</option>
              </select>
            </div>
            <div className="dam-field-row">
              <NumField label="E<sub>t</sub> from E<sub>v</sub>" unit="eV" value={trap.Et_eV}
                onChange={v => updateIfaceTrap(trap.id, { Et_eV: v })} step={0.01} />
              <SciField label="N<sub>it</sub>" unit="cm⁻²" value={trap.Nit}
                onChange={v => updateIfaceTrap(trap.id, { Nit: v })} />
            </div>
            <div className="dam-field-row">
              <SciField label="&sigma;<sub>n</sub>" unit="cm²" value={trap.sigma_n}
                onChange={v => updateIfaceTrap(trap.id, { sigma_n: v })} />
              <SciField label="&sigma;<sub>p</sub>" unit="cm²" value={trap.sigma_p}
                onChange={v => updateIfaceTrap(trap.id, { sigma_p: v })} />
            </div>
          </div>
        ))}

        <div className="dam-divider" />
        <div className="dam-subsection-label">DFT Data Import</div>
        <button className="dam-btn dam-btn-ghost" disabled style={{ opacity: 0.5 }}>
          Import D<sub>it</sub>(E) from file
        </button>
        <div className="dam-hint" style={{ marginTop: 4 }}>Upload DFT-computed interface DOS &mdash; coming soon</div>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// DERIVED BC PANEL (for contact domains)
// ═════════════════════════════════════════════════════════════════════════════
function DerivedBCPanel({ layers, side, contacts, onContactsChange }) {
  // Derive effective WF and SRV from contact layer stack
  const refLayer = side === 'front' ? layers[layers.length - 1] : layers[0];
  const derivedWF = refLayer
    ? (refLayer.Na > refLayer.Nd ? refLayer.chi + refLayer.Eg : refLayer.chi)
    : null;

  const overrideKey = `${side}_override`;
  const isOverride = contacts[overrideKey] || false;

  return (
    <div style={{ margin: '12px 16px', padding: 12, background: 'var(--bg-2)', borderRadius: 6, border: '1px solid var(--border)' }}>
      <div className="dam-subsection-label">Derived Contact BC</div>
      <div className="dam-hint" style={{ marginBottom: 8 }}>
        Computed from your contact layer stack and passed to the solver.
      </div>
      {derivedWF !== null && (
        <>
          <div className="dam-field-row">
            <SciField label="WF<sub>eff</sub>" unit="eV" value={derivedWF} readOnly compact />
          </div>
          <div className="dam-hint">
            {refLayer.name}: &chi;{refLayer.Na > refLayer.Nd ? '+Eg' : ''} = {derivedWF.toFixed(2)} eV
          </div>
        </>
      )}
      <div className="dam-divider" />
      <div className="dam-toggle-row">
        <span>Override derived BC</span>
        <label className="dam-toggle">
          <input type="checkbox" checked={isOverride}
            onChange={e => onContactsChange({ ...contacts, [overrideKey]: e.target.checked })} />
          <span className="dam-toggle-slider" />
        </label>
      </div>
      {isOverride && (
        <>
          <NumField label="WF" unit="eV" value={contacts[`${side}_WF`]}
            onChange={v => onContactsChange({ ...contacts, [`${side}_WF`]: v })} step={0.05} min={2} max={7} />
          <div className="dam-field-row">
            <SciField label="S<sub>n</sub>" unit="cm/s" value={contacts[`${side}_Sn`]}
              onChange={v => onContactsChange({ ...contacts, [`${side}_Sn`]: v })} compact />
            <SciField label="S<sub>p</sub>" unit="cm/s" value={contacts[`${side}_Sp`]}
              onChange={v => onContactsChange({ ...contacts, [`${side}_Sp`]: v })} compact />
          </div>
        </>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// TRAP → LIFETIME LINK (v1.1.0)
// The τ_n / τ_p fields are BACKGROUND lifetimes (SRH centres not listed
// explicitly). Each trap level on the Defects tab is a separate,
// energy-resolved SRH centre in the solver (own n1/p1, no Matthiessen fold).
// This mirrors backend `_apply_bulk_traps` low-injection estimate so the
// user sees how the defects shorten the minority lifetime BEFORE running.
// ═════════════════════════════════════════════════════════════════════════════
const _KB = 1.381e-23, _M0 = 9.109e-31;
function _repNt(t) {
  if (Array.isArray(t.Nt_profile) && t.Nt_profile.length >= 2) {
    return Math.max(...t.Nt_profile.map(n => Number(n.value) || 0));
  }
  return Number(t.Nt) || 0;
}
export function estimateTrapLifetimes(layer, T = 300) {
  const traps = (layer?.traps || []).filter(t => _repNt(t) > 0 && ((t.sigma_n || 0) > 0 || (t.sigma_p || 0) > 0));
  const num = v => (Array.isArray(v) ? Math.max(...v.map(n => Number(n.value) || 0)) : Number(v) || 0);
  const tauBgN = num(layer?.tau_n_ns) * 1e-9, tauBgP = num(layer?.tau_p_ns) * 1e-9;
  const Eg = num(layer?.Eg) || 1.5;
  const me = Number(layer?.m_e_eff) || 0.1, mh = Number(layer?.m_h_eff) || 0.5;
  const kT = 8.617333e-5 * T;
  const Nc = 2.509e19 * Math.pow(me * T / 300, 1.5), Nv = 2.509e19 * Math.pow(mh * T / 300, 1.5);
  const vthE = Math.sqrt(3 * _KB * T / (me * _M0)) * 100, vthH = Math.sqrt(3 * _KB * T / (mh * _M0)) * 100;
  const Na = num(layer?.Na), Nd = num(layer?.Nd);
  const pType = Na >= Nd; const Nmaj = Math.max(Na, Nd, 1);
  let invN = tauBgN > 0 ? 1 / tauBgN : 0, invP = tauBgP > 0 ? 1 / tauBgP : 0;
  const levels = [];
  for (const t of traps) {
    const Nt = _repNt(t);
    const sn = Number(t.sigma_n) || 0, sp = Number(t.sigma_p) || 0;
    let EtV = t.Et_ref === 'Ec' ? Eg - (Number(t.Et) || 0) : (Number(t.Et) || 0);
    if (!(EtV > 0 && EtV < Eg)) EtV = 0.5 * Eg;
    const n1 = Nc * Math.exp(-(Eg - EtV) / kT), p1 = Nv * Math.exp(-EtV / kT);
    const tn0 = sn > 0 ? 1 / (sn * vthE * Nt) : Infinity, tp0 = sp > 0 ? 1 / (sp * vthH * Nt) : Infinity;
    let tmin;
    if (pType) {
      tmin = tn0 * (1 + p1 / Nmaj) + tp0 * (n1 / Nmaj);
      if (Number.isFinite(tmin) && tmin > 0) invN += 1 / tmin;
      if (Number.isFinite(tp0)) invP += 1 / tp0;
    } else {
      tmin = tp0 * (1 + n1 / Nmaj) + tn0 * (p1 / Nmaj);
      if (Number.isFinite(tmin) && tmin > 0) invP += 1 / tmin;
      if (Number.isFinite(tn0)) invN += 1 / tn0;
    }
    levels.push({ EtV, tauMinNs: Number.isFinite(tmin) ? tmin * 1e9 : Infinity, tn0Ns: tn0 * 1e9, tp0Ns: tp0 * 1e9 });
  }
  return {
    nTraps: traps.length, pType, Eg,
    tauBgNns: tauBgN * 1e9, tauBgPns: tauBgP * 1e9,
    tauEffNns: invN > 0 ? 1e9 / invN : Infinity,
    tauEffPns: invP > 0 ? 1e9 / invP : Infinity,
    levels,
  };
}
function _fmtNs(x) {
  if (!Number.isFinite(x)) return '∞';
  if (x >= 1000) return (x / 1000).toPrecision(3) + ' µs';
  if (x >= 1) return x.toPrecision(3) + ' ns';
  return (x * 1000).toPrecision(3) + ' ps';
}
function TrapLifetimeSummary({ layer, compact = false }) {
  const est = estimateTrapLifetimes(layer);
  const minLbl = est.pType ? 'τn (minority electrons)' : 'τp (minority holes)';
  const bg = est.pType ? est.tauBgNns : est.tauBgPns;
  const eff = est.pType ? est.tauEffNns : est.tauEffPns;
  const shortened = est.nTraps > 0 && Number.isFinite(eff) && bg > 0 && eff < 0.999 * bg;
  return (
    <div className="dam-muted" style={{ fontSize: 11, lineHeight: 1.5, marginTop: compact ? 4 : 8 }}>
      {est.nTraps === 0 ? (
        <>τ<sub>n</sub>/τ<sub>p</sub> above are <b>background</b> SRH lifetimes (no explicit trap
        levels — add them on the Defects tab; each level becomes its own energy-resolved SRH centre
        in the solver, so a shallow level costs far less than a mid-gap one).</>
      ) : (
        <>
          {est.nTraps} trap level{est.nTraps !== 1 ? 's' : ''} + background τ →{' '}
          <b>{minLbl} ≈ {_fmtNs(eff)}</b> (background {_fmtNs(bg)}
          {shortened ? `, ${(100 * (1 - eff / bg)).toFixed(0)}% shorter` : ', unchanged — levels too shallow / dilute to matter'})
          {' '}— low-injection estimate at N<sub>maj</sub>; the solver resolves each level's
          occupancy vs bias.
          {!compact && est.levels.length > 0 && (
            <div style={{ marginTop: 3 }}>
              {est.levels.map((l, i) => (
                <div key={i}>
                  &nbsp;&nbsp;level {i + 1}: E<sub>v</sub>+{l.EtV.toFixed(2)} eV
                  {' '}(E<sub>c</sub>−{(est.Eg - l.EtV).toFixed(2)}) → τ<sub>n0</sub> {_fmtNs(l.tn0Ns)},
                  τ<sub>p0</sub> {_fmtNs(l.tp0Ns)}, minority-limited {_fmtNs(l.tauMinNs)}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// LAYER PARAMETER EDITOR (5-tab: Basic / Transport / Doping / Defects / Grading)
// ═════════════════════════════════════════════════════════════════════════════
function LayerParameterEditor({
  layer, layerIndex, totalLayers, onChange, onAutoPopulate,
  Nc, Nv, ni,
  onAddTrap, onUpdateTrap, onRemoveTrap,
  onSetUniform, onSetGraded, onSetProfile,
}) {
  const [tab, setTab] = useState('basic');
  const set = (key, val) => onChange({ [key]: val });

  const tabs = [
    { id: 'basic',     label: 'Basic' },
    { id: 'transport', label: 'Transport' },
    { id: 'doping',    label: 'Doping & Recomb.' },
    { id: 'defects',   label: 'Defects' },
  ];

  const materialType = layer.Nd > layer.Na ? 'n-type' : (layer.Na > layer.Nd ? 'p-type' : 'intrinsic');

  return (
    <div className="dam-editor">
      <div className="dam-editor-header">
        <div className="dam-editor-title">{layer.name}</div>
        <button className="dam-btn dam-btn-secondary dam-btn-sm" onClick={onAutoPopulate}
          title="Fill literature defaults for this material">Auto-populate</button>
      </div>

      <div className="dam-editor-tabs">
        {tabs.map(t => (
          <button key={t.id}
            className={`dam-tab ${tab === t.id ? 'active' : ''}`}
            onClick={() => setTab(t.id)}>{t.label}</button>
        ))}
      </div>

      <div className="dam-editor-body">
        {/* ── TAB 1: Basic Properties ──────────────────────── */}
        {tab === 'basic' && (
          <div className="dam-tab-content">
            <TextField label="Layer Name" value={layer.name}
              onChange={v => set('name', v)} />

            <NumField label="Thickness" unit="nm"
              value={Math.round((layer.thickness_um || 0) * 1000)}
              onChange={v => set('thickness_um', v / 1000)} step={1} min={1} />

            <div className="dam-field">
              <div className="dam-field-label"><span>Material Preset</span></div>
              <div style={{ display: 'flex', gap: 6 }}>
                <select className="dam-field-input" value={layer.material} style={{ flex: 1 }}
                  onChange={e => {
                    const mat = e.target.value;
                    const d = MAT_DEFAULTS[mat] || MAT_DEFAULTS.custom;
                    onChange({
                      material: mat, name: MAT_DISPLAY[mat] || mat,
                      ...d, traps: layer.traps,
                    });
                  }}>
                  {Object.entries(MATERIAL_CATEGORIES).map(([cat, mats]) => (
                    <optgroup key={cat} label={cat.toUpperCase()}>
                      {mats.map(m => <option key={m} value={m}>{MAT_DISPLAY[m] || m}</option>)}
                    </optgroup>
                  ))}
                </select>
                <button className="dam-btn dam-btn-accent dam-btn-sm" onClick={onAutoPopulate}
                  title="Auto-populate">Fill</button>
              </div>
            </div>

            <div className="dam-field-row">
              <GradableField label="E<sub>g</sub>" unit="eV" prop="Eg"
                layer={layer} onChange={v => set('Eg', v)}
                onSetUniform={onSetUniform} onSetGraded={onSetGraded}
                onSetProfile={onSetProfile} step={0.01} min={0.1} max={10} />
              <GradableField label="&chi;" unit="eV" prop="chi"
                layer={layer} onChange={v => set('chi', v)}
                onSetUniform={onSetUniform} onSetGraded={onSetGraded}
                onSetProfile={onSetProfile} step={0.01} min={0} max={10} />
            </div>

            <GradableField label="&epsilon;<sub>r</sub>" unit="" prop="eps_r"
              layer={layer} onChange={v => set('eps_r', v)}
              onSetUniform={onSetUniform} onSetGraded={onSetGraded}
              onSetProfile={onSetProfile} step={0.1} min={1} max={100} />

            <div className="dam-field">
              <div className="dam-field-label"><span>Material Type</span></div>
              <div className="dam-type-badge" style={{
                color: materialType === 'n-type' ? 'var(--accent)' : materialType === 'p-type' ? 'var(--green)' : 'var(--text-2)'
              }}>
                {materialType}
              </div>
            </div>
          </div>
        )}

        {/* ── TAB 2: Transport ─────────────────────────────── */}
        {tab === 'transport' && (
          <div className="dam-tab-content">
            <div className="dam-field-row">
              <GradableField label="&mu;<sub>e</sub>" unit="cm²/Vs" prop="mu_e"
                layer={layer} onChange={v => set('mu_e', v)}
                onSetUniform={onSetUniform} onSetGraded={onSetGraded}
                onSetProfile={onSetProfile} step={1} min={0.0001} />
              <GradableField label="&mu;<sub>h</sub>" unit="cm²/Vs" prop="mu_h"
                layer={layer} onChange={v => set('mu_h', v)}
                onSetUniform={onSetUniform} onSetGraded={onSetGraded}
                onSetProfile={onSetProfile} step={1} min={0.0001} />
            </div>

            <Collapsible title="Advanced — Effective Masses">
              <div className="dam-field-row">
                <NumField label="m<sub>e</sub>*" unit="m&#8320;" value={layer.m_e_eff}
                  onChange={v => set('m_e_eff', v)} step={0.01} min={0.001} />
                <NumField label="m<sub>h</sub>*" unit="m&#8320;" value={layer.m_h_eff}
                  onChange={v => set('m_h_eff', v)} step={0.01} min={0.001} />
              </div>
            </Collapsible>

            <div className="dam-subsection-label">Computed Densities of States</div>
            <div className="dam-field-row">
              <SciField label="N<sub>c</sub>" unit="cm⁻³" value={Nc} readOnly />
              <SciField label="N<sub>v</sub>" unit="cm⁻³" value={Nv} readOnly />
            </div>
            <SciField label="n<sub>i</sub>" unit="cm⁻³" value={ni} readOnly />
          </div>
        )}

        {/* ── TAB 3: Doping & Recombination ────────────────── */}
        {tab === 'doping' && (
          <div className="dam-tab-content">
            <GradableField label="N<sub>d</sub> (donors)" unit="cm⁻³" prop="Nd"
              layer={layer} onChange={v => set('Nd', v)}
              onSetUniform={onSetUniform} onSetGraded={onSetGraded}
              onSetProfile={onSetProfile} sci />
            <GradableField label="N<sub>a</sub> (acceptors)" unit="cm⁻³" prop="Na"
              layer={layer} onChange={v => set('Na', v)}
              onSetUniform={onSetUniform} onSetGraded={onSetGraded}
              onSetProfile={onSetProfile} sci />
            <div className="dam-divider" />
            <GradableField label="&tau;<sub>n</sub> (electron)" unit="ns" prop="tau_n_ns"
              layer={layer} onChange={v => set('tau_n_ns', v)}
              onSetUniform={onSetUniform} onSetGraded={onSetGraded}
              onSetProfile={onSetProfile} sci />
            <GradableField label="&tau;<sub>p</sub> (hole)" unit="ns" prop="tau_p_ns"
              layer={layer} onChange={v => set('tau_p_ns', v)}
              onSetUniform={onSetUniform} onSetGraded={onSetGraded}
              onSetProfile={onSetProfile} sci />
            <TrapLifetimeSummary layer={layer} compact />

            <Collapsible title="Advanced — Radiative Recombination">
              <SciField label="B (radiative)" unit="cm³/s" value={layer.B_rad ?? 2e-10}
                onChange={v => set('B_rad', v)} />
            </Collapsible>
          </div>
        )}

        {/* ── TAB 4: Defects (Traps) ───────────────────────── */}
        {tab === 'defects' && (
          <div className="dam-tab-content">
            <div className="dam-defects-header">
              <span>Trap Levels ({(layer.traps || []).length})</span>
              <button className="dam-btn dam-btn-accent dam-btn-sm" onClick={onAddTrap}>
                + Add Trap Level
              </button>
            </div>
            <TrapLifetimeSummary layer={layer} />
            {(layer.traps || []).length === 0 ? (
              <div className="dam-muted">No traps defined &mdash; layer uses the background τ<sub>n</sub>/τ<sub>p</sub> SRH only</div>
            ) : (
              (layer.traps || []).map((trap, i) => (
                <div key={trap.id} className="dam-trap-card">
                  <div className="dam-trap-header">
                    <span>Trap {i + 1}</span>
                    <button className="dam-action-btn danger"
                      onClick={() => onRemoveTrap(trap.id)}>&times;</button>
                  </div>
                  <div className="dam-field">
                    <div className="dam-field-label"><span>Type</span></div>
                    <select className="dam-field-input" value={trap.type}
                      onChange={e => onUpdateTrap(trap.id, { type: e.target.value })}>
                      <option value="donor">Donor</option>
                      <option value="acceptor">Acceptor</option>
                      <option value="neutral">Neutral</option>
                    </select>
                  </div>
                  <div className="dam-field-row">
                    <div className="dam-field">
                      <div className="dam-field-label"><span>Energy Reference</span></div>
                      <select className="dam-field-input" value={trap.Et_ref}
                        onChange={e => onUpdateTrap(trap.id, { Et_ref: e.target.value })}>
                        <option value="Ev">From E_v</option>
                        <option value="Ec">From E_c</option>
                      </select>
                    </div>
                    <NumField label="E<sub>t</sub>" unit="eV" value={trap.Et}
                      onChange={v => onUpdateTrap(trap.id, { Et: v })} step={0.01} />
                  </div>
                  <div className="dam-field-row">
                    <SciField label="&sigma;<sub>n</sub>" unit="cm²" value={trap.sigma_n}
                      onChange={v => onUpdateTrap(trap.id, { sigma_n: v })} />
                    <SciField label="&sigma;<sub>p</sub>" unit="cm²" value={trap.sigma_p}
                      onChange={v => onUpdateTrap(trap.id, { sigma_p: v })} />
                  </div>
                  <TrapNtField trap={trap} layer={layer}
                    onUpdate={u => onUpdateTrap(trap.id, u)} />
                </div>
              ))
            )}
          </div>
        )}

      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// GRADABLE FIELD — toggles between Uniform (single value) and Graded (profile)
// ═════════════════════════════════════════════════════════════════════════════
function GradableField({
  label, unit, prop, layer, onChange,
  onSetUniform, onSetGraded, onSetProfile,
  step = 0.01, min, max, sci = false,
}) {
  const profile = layer[`${prop}_profile`];
  const isGraded = Array.isArray(profile) && profile.length >= 2;
  const value = layer[prop];
  const thick_nm = Math.round((layer.thickness_um || 1) * 1000);

  return (
    <div className="dam-field" style={{ marginBottom: 6 }}>
      <div className="dam-field-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>
          <span dangerouslySetInnerHTML={{ __html: label }} />
          {unit && <span className="dam-field-unit"> ({unit})</span>}
        </span>
        <div className="dam-toggle-group" style={{ width: 'auto', fontSize: 10 }}>
          <button type="button"
            className={`dam-toggle-btn ${!isGraded ? 'active' : ''}`}
            onClick={() => onSetUniform(prop)}>Uniform</button>
          <button type="button"
            className={`dam-toggle-btn ${isGraded ? 'active' : ''}`}
            onClick={() => onSetGraded(prop)}>Graded</button>
        </div>
      </div>
      {isGraded ? (
        <ProfileEditor profile={profile} thick_nm={thick_nm} sci={sci}
          onChange={prof => onSetProfile(prop, prof)} />
      ) : (
        <FreeNumInput value={value} onCommit={onChange} sci={sci}
          min={min} max={max} />
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// TRAP DENSITY FIELD — Uniform/Graded toggle for one bulk trap's Nt,
// reusing the same ProfileEditor (graph + table) the layer's gradable
// properties use. Profile positions are measured from the OWNING LAYER's
// front face and span its thickness, matching the backend
// BulkTrapInput.Nt_profile convention. Only the density grades — trap
// energy and capture cross-sections stay uniform.
// ═════════════════════════════════════════════════════════════════════════════
function TrapNtField({ trap, layer, onUpdate }) {
  const profile = trap.Nt_profile;
  const isGraded = Array.isArray(profile) && profile.length >= 2;
  const thick_nm = Math.round((layer.thickness_um || 1) * 1000);

  return (
    <div className="dam-field" style={{ marginBottom: 6 }}>
      <div className="dam-field-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>
          N<sub>t</sub><span className="dam-field-unit"> (cm⁻³)</span>
        </span>
        <div className="dam-toggle-group" style={{ width: 'auto', fontSize: 10 }}>
          <button type="button"
            className={`dam-toggle-btn ${!isGraded ? 'active' : ''}`}
            onClick={() => onUpdate({ Nt_profile: undefined })}>Uniform</button>
          <button type="button"
            className={`dam-toggle-btn ${isGraded ? 'active' : ''}`}
            onClick={() => {
              if (isGraded) return;
              const v = trap.Nt ?? 1e14;
              onUpdate({
                Nt_profile: [
                  { id: genId(), position_nm: 0,        value: v },
                  { id: genId(), position_nm: thick_nm, value: v },
                ],
              });
            }}>Graded</button>
        </div>
      </div>
      {isGraded ? (
        <ProfileEditor profile={profile} thick_nm={thick_nm} sci
          onChange={prof => onUpdate({ Nt_profile: prof })} />
      ) : (
        <FreeNumInput value={trap.Nt} sci
          onCommit={v => onUpdate({ Nt: v })} min={0} />
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// PROFILE GRAPH EDITOR — visual (SVG) editor for a graded-property profile.
// Renders the layer cross-section (x = position through the layer, width =
// thickness) with the profile as a polyline and one draggable marker per
// node. Edits the SAME {position_nm, value} array the table below edits
// (single source of truth — both stay in sync automatically):
//   · drag a marker  — horizontal → position_nm, vertical → value
//   · click on/near the curve — add a node at that position
//   · double-click a marker — delete it (min 2 nodes)
// For sci-formatted properties with all-positive values (doping,
// lifetimes) the vertical axis is log-scaled.
// ═════════════════════════════════════════════════════════════════════════════
function ProfileGraphEditor({ profile, thick_nm, sci, onChange }) {
  const svgRef = useRef(null);
  const profileRef = useRef(profile);
  profileRef.current = profile;
  const dragIdRef = useRef(null);
  const movedRef = useRef(false);
  const [activeInfo, setActiveInfo] = useState(null);

  // Every node needs a stable id for drag/delete tracking. Nodes created
  // by the table already carry one; preset-loaded profiles may not.
  useEffect(() => {
    if (profile.some(n => !n.id)) {
      const next = profile.map(n => n.id ? n : { ...n, id: genId() });
      profileRef.current = next;
      onChange(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  const W = 280, H = 130;
  const padL = 8, padR = 8, padT = 12, padB = 18;
  const span = Math.max(thick_nm, 1);

  const sorted = [...profile].sort((a, b) => a.position_nm - b.position_nm);
  const useLog = !!sci && sorted.length > 0 && sorted.every(n => n.value > 0);
  const tv = v => useLog ? Math.log10(v) : v;
  const fv = v => useLog ? Math.pow(10, v) : v;

  const tVals = sorted.map(n => tv(n.value)).filter(Number.isFinite);
  let vmin = tVals.length ? Math.min(...tVals) : 0;
  let vmax = tVals.length ? Math.max(...tVals) : 1;
  if (vmax - vmin < 1e-9) {
    const m = Math.abs(vmax) * 0.15 || 0.5;
    vmin -= m; vmax += m;
  } else {
    const m = (vmax - vmin) * 0.18;
    vmin -= m; vmax += m;
  }

  const X = pos => padL + (pos / span) * (W - padL - padR);
  const Y = val => {
    const t = (tv(val) - vmin) / (vmax - vmin);
    return padT + (1 - Math.min(Math.max(t, 0), 1)) * (H - padT - padB);
  };
  const invX = sx =>
    Math.min(Math.max(((sx - padL) / (W - padL - padR)) * span, 0), span);
  const invY = sy => {
    const clamped = Math.min(Math.max(sy, padT), H - padB);
    return fv(vmin + (1 - (clamped - padT) / (H - padT - padB)) * (vmax - vmin));
  };

  // Sensible display rounding: 3 sig figs for sci/log values, 3 decimals
  // for ordinary ones (Eg, chi, eps_r).
  const roundVal = v => (useLog || Math.abs(v) >= 1e4 || (v !== 0 && Math.abs(v) < 1e-2))
    ? Number(v.toPrecision(3))
    : Math.round(v * 1000) / 1000;

  // Linear interpolation on the profile (held flat beyond first/last node).
  function valueAt(pos) {
    if (sorted.length === 0) return 0;
    if (pos <= sorted[0].position_nm) return sorted[0].value;
    const last = sorted[sorted.length - 1];
    if (pos >= last.position_nm) return last.value;
    for (let i = 1; i < sorted.length; i++) {
      if (pos <= sorted[i].position_nm) {
        const a = sorted[i - 1], b = sorted[i];
        const f = (pos - a.position_nm) /
                  Math.max(b.position_nm - a.position_nm, 1e-9);
        return a.value + f * (b.value - a.value);
      }
    }
    return last.value;
  }

  const svgPoint = (e) => {
    const rect = svgRef.current.getBoundingClientRect();
    return {
      sx: (e.clientX - rect.left) * W / rect.width,
      sy: (e.clientY - rect.top) * H / rect.height,
    };
  };

  function handleNodeDown(e, node) {
    e.stopPropagation();
    e.preventDefault();
    movedRef.current = false;
    dragIdRef.current = node.id;
    setActiveInfo({ position_nm: node.position_nm, value: node.value });
    try { svgRef.current.setPointerCapture(e.pointerId); } catch { /* older browsers */ }
  }

  function handleMove(e) {
    if (!dragIdRef.current) return;
    movedRef.current = true;
    const { sx, sy } = svgPoint(e);
    const pos = Math.round(invX(sx));
    const val = roundVal(invY(sy));
    const next = profileRef.current
      .map(n => n.id === dragIdRef.current
        ? { ...n, position_nm: pos, value: val } : n)
      .sort((a, b) => a.position_nm - b.position_nm);
    profileRef.current = next;
    setActiveInfo({ position_nm: pos, value: val });
    onChange(next);
  }

  function handleUp() { dragIdRef.current = null; }

  function handleClick(e) {
    // Ignore the click that ends a drag, and the 2nd click of a dblclick.
    if (movedRef.current) { movedRef.current = false; return; }
    if (e.detail > 1) return;
    const { sx, sy } = svgPoint(e);
    const pos = Math.round(invX(sx));
    const v = valueAt(pos);
    if (Math.abs(Y(v) - sy) > 16) return;   // only add near the curve
    const node = { id: genId(), position_nm: pos, value: roundVal(v) };
    const next = [...profileRef.current, node]
      .sort((a, b) => a.position_nm - b.position_nm);
    profileRef.current = next;
    setActiveInfo({ position_nm: pos, value: node.value });
    onChange(next);
  }

  function handleNodeDblClick(e, node) {
    e.stopPropagation();
    e.preventDefault();
    if (profileRef.current.length <= 2) return;
    const next = profileRef.current.filter(n => n.id !== node.id);
    profileRef.current = next;
    setActiveInfo(null);
    onChange(next);
  }

  // Polyline: held flat from x=0 to the first node and from the last node
  // to the layer's back face — mirrors the solver's interpolation.
  const linePts = sorted.length > 0 ? [
    `${X(0)},${Y(sorted[0].value)}`,
    ...sorted.map(n => `${X(Math.min(n.position_nm, span))},${Y(n.value)}`),
    `${X(span)},${Y(sorted[sorted.length - 1].value)}`,
  ].join(' ') : '';

  const fmtNode = (n) => `${Math.round(n.position_nm)} nm · ${formatSci(n.value)}`;
  // Axis-range labels: vmin/vmax include unrounded padding margins — trim
  // to 4 significant figures so labels never spill full float precision.
  const fmtAxis = (v) => {
    if (!Number.isFinite(v)) return '';
    const a = Math.abs(v);
    if (a >= 1e4 || (a < 1e-2 && v !== 0)) return v.toExponential(2);
    return String(Number(v.toPrecision(4)));
  };

  return (
    <div style={{ marginBottom: 4 }}>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        style={{
          width: '100%', maxWidth: 460, display: 'block', touchAction: 'none',
          background: 'rgba(0,212,255,0.04)',
          border: '1px solid var(--border, #30363d)', borderRadius: 4,
          cursor: 'crosshair',
        }}
        onPointerMove={handleMove}
        onPointerUp={handleUp}
        onPointerLeave={handleUp}
        onClick={handleClick}
      >
        {/* layer cross-section frame */}
        <rect x={padL} y={padT} width={W - padL - padR} height={H - padT - padB}
          fill="none" stroke="var(--border, #30363d)" strokeDasharray="3 3" />
        {/* value range labels */}
        <text x={padL + 3} y={padT + 8} fontSize={8} fill="var(--text-2, #8b949e)"
          fontFamily="var(--mono)">{fmtAxis(fv(vmax))}</text>
        <text x={padL + 3} y={H - padB - 3} fontSize={8} fill="var(--text-2, #8b949e)"
          fontFamily="var(--mono)">{fmtAxis(fv(vmin))}</text>
        {/* position axis labels */}
        <text x={padL} y={H - 5} fontSize={8} fill="var(--text-2, #8b949e)"
          fontFamily="var(--mono)">0</text>
        <text x={W - padR} y={H - 5} fontSize={8} textAnchor="end"
          fill="var(--text-2, #8b949e)" fontFamily="var(--mono)">
          {Math.round(thick_nm)} nm
        </text>
        {/* profile curve */}
        {linePts && (
          <polyline points={linePts} fill="none"
            stroke="var(--accent, #00d4ff)" strokeWidth={1.5} />
        )}
        {/* draggable node markers */}
        {sorted.map(n => (
          <circle
            key={n.id || `${n.position_nm}-${n.value}`}
            cx={X(Math.min(n.position_nm, span))} cy={Y(n.value)} r={5}
            fill="var(--accent, #00d4ff)" stroke="var(--bg-0, #0d1117)"
            strokeWidth={1.5} style={{ cursor: 'grab' }}
            onPointerDown={e => handleNodeDown(e, n)}
            onDoubleClick={e => handleNodeDblClick(e, n)}
            onClick={e => e.stopPropagation()}
          >
            <title>{fmtNode(n)} — drag to move, double-click to delete</title>
          </circle>
        ))}
      </svg>
      <div style={{
        fontSize: 10, color: 'var(--text-2, #8b949e)',
        fontFamily: 'var(--mono)', marginTop: 2,
      }}>
        {activeInfo
          ? `node: ${Math.round(activeInfo.position_nm)} nm · ${formatSci(activeInfo.value)}${useLog ? ' (log scale)' : ''}`
          : `drag markers · click curve to add · double-click marker to delete${useLog ? ' · log scale' : ''}`}
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// PROFILE EDITOR — table of {position_nm, value} nodes
// ═════════════════════════════════════════════════════════════════════════════
function ProfileEditor({ profile, thick_nm, sci, onChange }) {
  function update(idx, patch) {
    const next = profile.map((n, i) => i === idx ? { ...n, ...patch } : n);
    next.sort((a, b) => a.position_nm - b.position_nm);
    onChange(next);
  }
  function addNode() {
    const last = profile[profile.length - 1];
    const next = [...profile, {
      id: genId(),
      position_nm: Math.min(thick_nm, (last?.position_nm ?? 0) + thick_nm * 0.25),
      value: last?.value ?? 0,
    }].sort((a, b) => a.position_nm - b.position_nm);
    onChange(next);
  }
  function removeNode(idx) {
    if (profile.length <= 2) return;
    onChange(profile.filter((_, i) => i !== idx));
  }
  return (
    <div className="dam-profile-table-wrap" style={{ marginTop: 4 }}>
      {/* Visual editor — same profile array as the table below, so the
          numeric cells are the live readout of every drag and vice versa. */}
      <ProfileGraphEditor profile={profile} thick_nm={thick_nm} sci={sci}
        onChange={onChange} />
      <table className="dam-profile-table">
        <thead>
          <tr><th>Pos (nm)</th><th>Value</th><th></th></tr>
        </thead>
        <tbody>
          {profile.map((n, idx) => (
            <tr key={n.id || idx}>
              <td>
                <FreeNumInput className="dam-table-input" value={n.position_nm}
                  min={0} max={thick_nm}
                  onCommit={v => update(idx, { position_nm: v })} />
              </td>
              <td>
                <FreeNumInput className="dam-table-input" sci={sci}
                  value={n.value}
                  onCommit={v => update(idx, { value: v })} />
              </td>
              <td>
                <button className="dam-action-btn danger dam-btn-sm"
                  disabled={profile.length <= 2}
                  onClick={() => removeNode(idx)}>&times;</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button className="dam-btn dam-btn-accent dam-btn-sm" style={{ marginTop: 4 }}
        onClick={addNode}>+ Add Node</button>
    </div>
  );
}

// Export material data for SimulatorPanel to use
export { MAT_DEFAULTS, MATERIAL_CATEGORIES, MAT_DISPLAY, LAYER_COLORS };
